import React, { useEffect, useRef, useState, useMemo } from 'react';
import { format, getDaysInMonth } from 'date-fns';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import FairPriceView from '@/components/polymer/FairPriceView';
import InventoryChangeView from '@/components/polymer/InventoryChangeView';
import RatesView from '@/components/polymer/RatesView';
import MarketsView from '@/components/polymer/MarketsView';
import UsitcChart from '@/components/polymer/UsitcChart';
import PolymerTotalsView from '@/components/polymer/PolymerTotalsView';
import DemandProductionCapacityChart from '@/components/charts/DemandProductionCapacityChart';
import StackedBarChart, { StackedDataset } from '@/components/charts/StackedBarChart';
import PolymerDashboardLayout from '@/components/polymer/PolymerDashboardLayout';
import FreeTrialFade from '@/components/common/FreeTrialFade';
import type { PolymerDashboardConfig } from '@/types/polymer-dashboard.types';
import { keepFirstEntryPerMonth } from '@/utils/commonUtils';
import { getCachedACCData, getPVCInventoryMetrics } from '@/services/marketIntelService';
import { useTheme } from '@/contexts/ThemeContext';
import { useRsColors } from '@/hooks/useRsColors';
import { createVerticalHoverLinePlugin } from '@/utils/chartPlugins';
import { chartColors } from '@/utils/chartColors';

const DEFAULT_DATE_FROM = (() => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 5);
  return d.toISOString().split('T')[0];
})();

const DEFAULT_DATE_TO = new Date().toISOString().split('T')[0];

type DateView = 'monthly' | 'daily';

const EXPORT_REGIONS = [
  { key: 'export_africa', label: 'Africa' },
  { key: 'export_canada', label: 'Canada' },
  { key: 'export_china', label: 'China' },
  { key: 'export_europe', label: 'Europe' },
  { key: 'export_latin_america', label: 'Latin America' },
  { key: 'export_middle_east', label: 'Middle East' },
  { key: 'export_mexico', label: 'Mexico' },
  { key: 'export_pacific_rim', label: 'Pacific Rim' },
  { key: 'export_Other', label: 'Other' },
];

const PVC_CONFIG: PolymerDashboardConfig = {
  polymer: 'PVC',
  polymerFullName: 'PVC',
  defaultTab: 'overview',
  tabs: [
    { key: 'overview', label: 'Overview' },
    { key: 'fair-price', label: 'Fair Resin Price' },
    { key: 'totals', label: 'Totals' },
    { key: 'inventory-change', label: 'Inventory Change' },
    { key: 'demand-production', label: 'Demand/Production/Capacity' },
    { key: 'rates', label: 'Rates' },
    { key: 'usitc', label: 'USITC' },
    { key: 'exports', label: 'Exports' },
    { key: 'markets', label: 'Markets' },
  ],
};

const ChartPanel: React.FC<{
  title: string;
  children: React.ReactNode;
  className?: string;
  actions?: React.ReactNode;
}> = ({ title, children, className, actions }) => (
  <div className={`bg-rs-off-black/60 backdrop-blur-sm border border-rs-jet-light/50 rounded-xl shadow-lg shadow-black/20 p-5 ${className || ''}`}>
    <div className="grid grid-cols-3 items-center mb-4">
      <h3 className="text-sm font-medium text-rs-off-white/70 uppercase tracking-wider whitespace-nowrap">
        {title}
      </h3>
      {actions && <div className="justify-self-center">{actions}</div>}
      <div></div>
    </div>
    {children}
  </div>
);

const PVCDashboard: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const { isDark } = useTheme();
  const rs = useRsColors();
  const hoverLinePlugin = useMemo(() => createVerticalHoverLinePlugin(rs), [rs]);
  const [accData, setAccData] = useState<any[]>([]);
  const [inventoryMetrics, setInventoryMetrics] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [exportDateView, setExportDateView] = useState<DateView>('monthly');
  const hasFetched = useRef(false);
  const [isSmallDpcSelector, setIsSmallDpcSelector] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 749px)').matches,
  );
  const [hideChartLabels, setHideChartLabels] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 1299px)').matches,
  );
  const [isSmallExports, setIsSmallExports] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 750px)').matches,
  );
  const [isSmallOverview, setIsSmallOverview] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 750px)').matches,
  );

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 749px)');
    const chartLabelsMq = window.matchMedia('(max-width: 1299px)');
    const exportsMq = window.matchMedia('(max-width: 750px)');
    const overviewMq = window.matchMedia('(max-width: 750px)');
    const handler = (e: MediaQueryListEvent) => setIsSmallDpcSelector(e.matches);
    const chartLabelsHandler = (e: MediaQueryListEvent) => setHideChartLabels(e.matches);
    const exportsHandler = (e: MediaQueryListEvent) => setIsSmallExports(e.matches);
    const overviewHandler = (e: MediaQueryListEvent) => setIsSmallOverview(e.matches);
    mq.addEventListener('change', handler);
    chartLabelsMq.addEventListener('change', chartLabelsHandler);
    exportsMq.addEventListener('change', exportsHandler);
    overviewMq.addEventListener('change', overviewHandler);
    return () => {
      mq.removeEventListener('change', handler);
      chartLabelsMq.removeEventListener('change', chartLabelsHandler);
      exportsMq.removeEventListener('change', exportsHandler);
      overviewMq.removeEventListener('change', overviewHandler);
    };
  }, []);

  useEffect(() => {
    if (hasFetched.current || !isAuthenticated) return;
    hasFetched.current = true;
    setLoading(true);
    Promise.all([
      getCachedACCData('pvc')
        .then((data) => setAccData(data))
        .catch(() => setAccData([])),
      getPVCInventoryMetrics()
        .then((data) => setInventoryMetrics(data))
        .catch(() => setInventoryMetrics([])),
    ]).finally(() => setLoading(false));
  }, [isAuthenticated]);

  const sortedDailyData = [...accData].sort((a, b) =>
    (a.date || a.record_date) < (b.date || b.record_date) ? -1 : 1
  );

  const monthlyData = keepFirstEntryPerMonth(sortedDailyData);
  // For daily view: keep only first day of each month (dedupe duplicates like two June entries)
  const dailyDataForChart = keepFirstEntryPerMonth(sortedDailyData);

  const monthlyLabels = monthlyData.map((d) => {
    try {
      return format(new Date((d.date || d.record_date) + 'T00:00:00'), 'MMM yyyy');
    } catch {
      return d.date;
    }
  });

  // Demand/Production/Capacity reads the import tables; the export-region
  // charts stay on accData, which is the only source for those columns.
  const invMonthly = keepFirstEntryPerMonth(
    [...inventoryMetrics].sort((a, b) =>
      (a.date || a.record_date) < (b.date || b.record_date) ? -1 : 1
    )
  );

  const invLabels = invMonthly.map((d) => {
    try {
      return format(new Date((d.date || d.record_date) + 'T00:00:00'), 'MMM yyyy');
    } catch {
      return d.date;
    }
  });

  const dailyLabels = dailyDataForChart.map((d) => {
    try {
      return format(new Date((d.date || d.record_date) + 'T00:00:00'), 'MMM yyyy');
    } catch {
      return d.date;
    }
  });

  const parsedDailyDates = dailyDataForChart.map((d) => {
    const dateString = String(d.date || d.record_date).slice(0, 10);

    try {
      return new Date(dateString + 'T00:00:00');
    } catch {
      return null;
    }
  });

  const daysInMonth = parsedDailyDates.map((parsedDate) =>
    parsedDate && !Number.isNaN(parsedDate.getTime()) ? getDaysInMonth(parsedDate) : 30
  );

  const exportLabels = exportDateView === 'monthly' ? monthlyLabels : dailyLabels;

  // Raw ACC data is a per-day export rate. Daily view shows it as-is;
  // Monthly view = daily rate × number of days in that month.
  const exportDatasetsDaily: StackedDataset[] = EXPORT_REGIONS.map((region) => ({
    label: region.label,
    data: dailyDataForChart.map((d) => {
      const value = d[region.key];
      return value !== null && value !== undefined ? Number(value) : 0;
    }),
  }));

  const exportDatasetsMonthly: StackedDataset[] = EXPORT_REGIONS.map((region) => ({
    label: region.label,
    data: dailyDataForChart.map((d, i) => {
      const value = d[region.key];
      const dailyRate = value !== null && value !== undefined ? Number(value) : 0;
      return dailyRate * daysInMonth[i];
    }),
  }));

  const exportDatasets = exportDateView === 'monthly' ? exportDatasetsMonthly : exportDatasetsDaily;

  const dateViewButtonClass = (active: boolean) =>
    `px-3 py-1.5 text-sm rounded font-medium transition-colors border ${
      active ? 'bg-rs-green text-black border-rs-green' : 'bg-rs-jet text-rs-off-white/70 hover:bg-rs-jet-light border-rs-jet-light'
    }`;

  const renderTabContent = (activeTab: string) => {
    if (activeTab === 'overview') {
      return (
        <div className="flex flex-col gap-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
            {isSmallOverview ? (
              <FairPriceView
                polymer="PVC"
                dateFrom={DEFAULT_DATE_FROM}
                dateTo={DEFAULT_DATE_TO}
                labelMode="overview"
                fullBleed
              />
            ) : (
              <ChartPanel title="Fair Resin Price" className="flex flex-col">
                <FairPriceView
                  polymer="PVC"
                  dateFrom={DEFAULT_DATE_FROM}
                  dateTo={DEFAULT_DATE_TO}
                  labelMode="overview"
                />
              </ChartPanel>
            )}
            {isSmallOverview ? (
              <div className="-mx-6">
                <h3 className="text-sm font-medium text-rs-off-white/70 uppercase tracking-wider mb-4 text-center px-4">
                  Demand, Production & Capacity
                </h3>
                <div className="h-[400px] px-2">
                  <DemandProductionCapacityChart
                    production={invMonthly.map((d) => d.domestic_production ?? null)}
                    exportsTotal={invMonthly.map((d) => d.exports ?? null)}
                    demand={invMonthly.map((d) => d.domestic_demand ?? null)}
                    capacity={invMonthly.map((d) => d.capacity ?? null)}
                    labels={invLabels}
                    polymer="PVC"
                    showDataLabels={false}
                    isOverviewPage={true}
                  />
                </div>
              </div>
            ) : (
              <ChartPanel title="Demand, Production & Capacity" className="flex flex-col">
                <div className="flex-1 flex flex-col min-h-0">
                  <DemandProductionCapacityChart
                    production={invMonthly.map((d) => d.domestic_production ?? null)}
                    exportsTotal={invMonthly.map((d) => d.exports ?? null)}
                    demand={invMonthly.map((d) => d.domestic_demand ?? null)}
                    capacity={invMonthly.map((d) => d.capacity ?? null)}
                    labels={invLabels}
                    polymer="PVC"
                    showDataLabels={false}
                    showBorder
                    isOverviewPage={true}
                  />
                </div>
              </ChartPanel>
            )}
          </div>
          {isSmallOverview ? (
            <div className="-mx-6">
              <div className="flex justify-center gap-1 mb-4 px-4" role="group" aria-label="Export date view">
                <button
                  type="button"
                  onClick={() => setExportDateView('monthly')}
                  aria-pressed={exportDateView === 'monthly'}
                  className={dateViewButtonClass(exportDateView === 'monthly')}
                >
                  Monthly
                </button>
                <button
                  type="button"
                  onClick={() => setExportDateView('daily')}
                  aria-pressed={exportDateView === 'daily'}
                  className={dateViewButtonClass(exportDateView === 'daily')}
                >
                  Daily
                </button>
              </div>
              <h3 className="text-sm font-medium text-rs-off-white/70 uppercase tracking-wider mb-4 text-center px-4">
                PVC Exports by Region
              </h3>
              <div className="h-[400px] px-2">
                <StackedBarChart
                  datasets={exportDatasets}
                  labels={exportLabels}
                  showDataLabels={false}
                  plugins={[hoverLinePlugin]}
                />
              </div>
            </div>
          ) : (
            <ChartPanel
              title="PVC Exports by Region"
              actions={
                  <div
                    className="flex gap-1"
                    role="group"
                    aria-label="Export date view"
                  >
                    <button
                      type="button"
                      onClick={() => setExportDateView('monthly')}
                      aria-pressed={exportDateView === 'monthly'}
                      className={dateViewButtonClass(exportDateView === 'monthly')}
                    >
                      Monthly
                    </button>

                    <button
                      type="button"
                      onClick={() => setExportDateView('daily')}
                      aria-pressed={exportDateView === 'daily'}
                      className={dateViewButtonClass(exportDateView === 'daily')}
                    >
                      Daily
                    </button>
                  </div>
              }
            >
              <div className="h-[400px]">
                <StackedBarChart
                  datasets={exportDatasets}
                  labels={exportLabels}
                  showDataLabels={false}
                  plugins={[hoverLinePlugin]}
                />
              </div>
            </ChartPanel>
          )}
        </div>
      );
    }

    switch (activeTab) {
      case 'fair-price':
        return (
          <FairPriceView
            polymer="PVC"
            dateFrom={DEFAULT_DATE_FROM}
            dateTo={DEFAULT_DATE_TO}
            labelMode="tab"
            fullBleed
          />
        );

      case 'totals':
        return (
          <PolymerTotalsView
            data={inventoryMetrics}
            polymer="PVC"
            fullBleed
          />
        );

      case 'inventory-change':
        return (
          <InventoryChangeView
            data={inventoryMetrics}
            inventoryChangeKey="inventory_change_monthly"
            inventoryChangeCumulativeKey="inventory_change_cumulative"
            polymer="PVC"
            fullBleed
          />
        );

      case 'demand-production':
        return (
          <div className={`flex flex-col gap-6 ${isSmallDpcSelector ? 'p-0 -mx-6' : 'p-4'}`}>
            <div className={`border border-rs-jet-light rounded-lg ${isSmallDpcSelector ? 'p-2' : 'p-4'} ${isDark ? 'bg-rs-off-black/40' : 'bg-rs-off-black'}`}>
              <h3 className={`text-rs-off-white font-medium text-sm mb-3 ${isSmallDpcSelector ? 'px-2 text-center' : ''}`}>
                Demand, Production & Capacity
              </h3>

              <div className="h-[700px]">
                <DemandProductionCapacityChart
                  production={invMonthly.map((d) => d.domestic_production ?? null)}
                  exportsTotal={invMonthly.map((d) => d.exports ?? null)}
                  demand={invMonthly.map((d) => d.domestic_demand ?? null)}
                  capacity={invMonthly.map((d) => d.capacity ?? null)}
                  labels={invLabels}
                  polymer="PVC"
                  showDataLabels={!hideChartLabels}
                />
              </div>
            </div>
          </div>
        );

      case 'rates':
        return <RatesView polymer="PVC" />;
      case 'usitc':
        return (
          <UsitcChart
            polymer="PVC"
            dateFrom={DEFAULT_DATE_FROM}
            dateTo={DEFAULT_DATE_TO}
          />
        );
      case 'exports':
        return (
          <div className={`flex flex-col gap-6 ${isSmallExports ? 'p-0 -mx-6' : ''}`}>
            <div className={`flex gap-1 mb-4 ${isSmallExports ? 'justify-center px-4' : ''}`} role="group" aria-label="Export date view">
              <button
                type="button"
                onClick={() => setExportDateView('monthly')}
                aria-pressed={exportDateView === 'monthly'}
                className={dateViewButtonClass(exportDateView === 'monthly')}
              >
                Monthly
              </button>
              <button
                type="button"
                onClick={() => setExportDateView('daily')}
                aria-pressed={exportDateView === 'daily'}
                className={dateViewButtonClass(exportDateView === 'daily')}
              >
                Daily
              </button>
            </div>

            <div className={`border border-rs-jet-light rounded-lg ${isSmallExports ? 'p-0' : 'p-4'} ${isDark ? 'bg-rs-off-black/40' : 'bg-rs-off-black'}`}>
              <h3 className={`text-sm font-medium text-rs-off-white/70 uppercase tracking-wider mb-3 ${isSmallExports ? 'text-center px-4 pt-4' : ''}`}>
                PVC Exports by Region
              </h3>
              {isSmallExports && (
                <div className="flex justify-center mb-3 px-4">
                  <div className="grid grid-cols-3 gap-x-6 gap-y-1.5 w-fit">
                    {exportDatasets.map((ds, i) => (
                      <div key={ds.label} className="flex items-center gap-2">
                        <span
                          className="inline-block w-3 h-3 rounded-sm flex-shrink-0"
                          style={{ backgroundColor: ds.color || chartColors.getColorByIndex(i) }}
                        />
                        <span className="text-rs-off-white text-xs whitespace-nowrap">{ds.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className={`h-[700px] ${isSmallExports ? 'px-4 pb-4' : ''}`}>
                <StackedBarChart
                  datasets={exportDatasets}
                  labels={exportLabels}
                  showDataLabels={false}
                  className="w-full h-full"
                  plugins={[hoverLinePlugin]}
                  hideLegend={isSmallExports}
                />
              </div>
            </div>
          </div>
        );
      case 'markets':
        return (
          <MarketsView
            polymer="PVC"
            data={accData}
          />
        );
      default:
        return null;
    }
  };

  if (isLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-rs-black">
        <Loader2 className="animate-spin opacity-30 w-16 h-16 text-rs-green" />
      </div>
    );
  }

  const renderContent = (activeTab: string) => {
    const blurredTabs = ['totals', 'inventory-change', 'demand-production', 'rates', 'markets'];
    return (
      <FreeTrialFade enabled={blurredTabs.includes(activeTab)}>
        {renderTabContent(activeTab)}
      </FreeTrialFade>
    );
  };

  return (
    <PolymerDashboardLayout
      config={PVC_CONFIG}
      renderContent={renderContent}
    />
  );
};

export default PVCDashboard;